'use client';

import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Grid3X3 } from 'lucide-react';

const MIN_TEAMS_PER_GROUP = 2;
const MAX_TEAMS_PER_GROUP = 8;

export interface LongGroupConfigFieldProps {
  teamsPerGroups: number;
  classifiedPerGroups: number;
  /** Emits the patch to apply. When shrinking the group size, qualifiers come already clamped. */
  onChange: (patch: { teamsPerGroups?: number; classifiedPerGroups?: number }) => void;
  disabled?: boolean;
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

/**
 * Group configuration for LONG tournaments with playoffs: size of each zone and how many
 * qualify for the knockout stage. Qualifiers can never reach the group size.
 *
 * Inputs keep their own text so they can be edited freely (clear and retype);
 * the value is emitted to the parent only when valid, and normalized (clamped) on blur.
 */
export function LongGroupConfigField({
  teamsPerGroups,
  classifiedPerGroups,
  onChange,
  disabled = false,
}: LongGroupConfigFieldProps) {
  const [teamsText, setTeamsText] = useState(String(teamsPerGroups));
  const [classifiedText, setClassifiedText] = useState(String(classifiedPerGroups));

  // Re-syncs when the value changes from the outside (e.g. changing format in the wizard).
  useEffect(() => setTeamsText(String(teamsPerGroups)), [teamsPerGroups]);
  useEffect(() => setClassifiedText(String(classifiedPerGroups)), [classifiedPerGroups]);

  const maxClassified = Math.max(1, teamsPerGroups - 1);

  const handleTeamsChange = (text: string) => {
    setTeamsText(text);
    const parsed = parseInt(text, 10);
    if (!Number.isNaN(parsed) && parsed >= MIN_TEAMS_PER_GROUP && parsed <= MAX_TEAMS_PER_GROUP) {
      const classified = clamp(classifiedPerGroups, 1, parsed - 1);
      onChange({ teamsPerGroups: parsed, classifiedPerGroups: classified });
    }
  };

  const handleTeamsBlur = () => {
    const parsed = parseInt(teamsText, 10);
    const teams = Number.isNaN(parsed)
      ? teamsPerGroups
      : clamp(parsed, MIN_TEAMS_PER_GROUP, MAX_TEAMS_PER_GROUP);
    setTeamsText(String(teams));
    const classified = clamp(classifiedPerGroups, 1, teams - 1);
    if (teams !== teamsPerGroups || classified !== classifiedPerGroups) {
      onChange({ teamsPerGroups: teams, classifiedPerGroups: classified });
    }
  };

  const handleClassifiedChange = (text: string) => {
    setClassifiedText(text);
    const parsed = parseInt(text, 10);
    if (!Number.isNaN(parsed) && parsed >= 1 && parsed <= maxClassified) {
      onChange({ classifiedPerGroups: parsed });
    }
  };

  const handleClassifiedBlur = () => {
    const parsed = parseInt(classifiedText, 10);
    const classified = Number.isNaN(parsed)
      ? classifiedPerGroups
      : clamp(parsed, 1, maxClassified);
    setClassifiedText(String(classified));
    if (classified !== classifiedPerGroups) onChange({ classifiedPerGroups: classified });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Grid3X3 className="h-5 w-5" />
          Configuración de grupos
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-6 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="long-teams-per-group">Equipos por grupo</Label>
            <Input
              id="long-teams-per-group"
              type="number"
              inputMode="numeric"
              min={MIN_TEAMS_PER_GROUP}
              max={MAX_TEAMS_PER_GROUP}
              value={teamsText}
              disabled={disabled}
              onChange={(e) => handleTeamsChange(e.target.value)}
              onBlur={handleTeamsBlur}
            />
            <p className="text-xs text-gray-500">
              Cantidad de equipos en cada zona (entre {MIN_TEAMS_PER_GROUP} y {MAX_TEAMS_PER_GROUP}).
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="long-classified-per-group">Clasificados a eliminatorias</Label>
            <Input
              id="long-classified-per-group"
              type="number"
              inputMode="numeric"
              min={1}
              max={maxClassified}
              value={classifiedText}
              disabled={disabled}
              onChange={(e) => handleClassifiedChange(e.target.value)}
              onBlur={handleClassifiedBlur}
            />
            <p className="text-xs text-gray-500">
              Cuántos avanzan a los cruces por zona (hasta {maxClassified} de {teamsPerGroups}).
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
